import {
  MICROSOFT365_TOOL_FEATURE,
  type Microsoft365Feature,
  type Microsoft365SendResultDto,
  type Microsoft365StatusDto,
  type Microsoft365WriteKind,
  type Microsoft365WriteOutcome,
  type Microsoft365WriteTool,
} from '@dbrain/shared';
import { writeState, type WriteState } from './microsoft365-write.js';

/**
 * The write cards' rules in the browser, without the DOM (part 3; MsSohbetOnay, MsToplantiOnay): how a card stands
 * between the click and the event that resolves it, why "Gönder" did not go (the card stays pending and nothing was
 * written), and which permission "Yeniden bağlan" asks for. Texts are keys under `microsoft365.chat.write`.
 */

export type CardPhase = WriteState | 'sending';

/**
 * How the card stands now: the event's outcome once it came; before that the send route's own answer (so the card
 * does not fall back to "pending" while the event is on its way), "Vazgeç" pressed, or "sending" while the click is
 * on its way. An approval that ended without an outcome never reads as sent (writeState).
 */
export function cardPhase(input: {
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled';
  outcome: Microsoft365WriteOutcome | null;
  sent?: Pick<Microsoft365SendResultDto, 'outcome'> | null;
  sending?: boolean;
  cancelled?: boolean;
}): CardPhase {
  if (input.status !== 'pending') return writeState(input.status, input.outcome);
  if (input.sent) return input.sent.outcome;
  if (input.cancelled) return 'cancelled';
  return input.sending ? 'sending' : 'pending';
}

/** The outcome the card shows: the event's, else the send route's answer (the same facts, a moment earlier). */
export function shownOutcome(outcome: Microsoft365WriteOutcome | null, sent: Microsoft365SendResultDto | null | undefined): Microsoft365WriteOutcome | null {
  if (outcome) return outcome;
  if (!sent) return null;
  return { outcome: sent.outcome, at: sent.at, payload: sent.payload, hash: sent.hash, ...(sent.code ? { code: sent.code } : {}), ...(sent.online !== undefined ? { online: sent.online } : {}) };
}

/** The switch a write tool needs (sending for the three mail tools, meetings for the meeting tool). */
export const featureOf = (tool: Microsoft365WriteTool): Microsoft365Feature => MICROSOFT365_TOOL_FEATURE[tool];

export interface PendingReason {
  /** `microsoft365.chat.write.pending.<key>` */
  key: string;
  /** upgrade: "Yeniden bağlan" asking for the card's permission; reconnect: a plain sign-in; consent: "Yöneticiye bildir" */
  action: 'upgrade' | 'reconnect' | 'consent' | null;
  /**
   * The click may have reached the server (no answer came) or the card was decided elsewhere: no second "Gönder" here;
   * the card's real state comes with its event (a reload shows it), and nothing is ever sent again by itself.
   */
  blocksSend: boolean;
}

/**
 * Why a click on "Gönder" did not send: the server refused before anything was written, so the card stays pending
 * ("Yeni izin gerekiyor · yeniden bağlan", "Firmanız mail göndermeyi kapattı", "Deneme kipinde yalnız izin verilen
 * adreslere gönderilebilir"…), or no answer came. Null: an error the generic alert words.
 */
export function pendingReason(
  code: string | null | undefined,
  kind: Microsoft365WriteKind,
  status?: Microsoft365StatusDto,
  detail: { fields?: readonly { path: string; code: string }[]; edited?: boolean } = {},
): PendingReason | null {
  switch (code) {
    case 'microsoft_scopes_required':
      return { key: 'scopes', action: 'upgrade', blocksSend: false };
    case 'microsoft_not_available': {
      // the firm's switch for sending (meetings), or Microsoft 365 itself off for the person (their own switch first)
      if (status?.enabled === false) return { key: 'off', action: null, blocksSend: false };
      if (status?.state === 'off_team') return { key: 'offTeam', action: null, blocksSend: false };
      if (status && status.state !== 'connected') return { key: 'off', action: null, blocksSend: false };
      return { key: `featureOff.${kind}`, action: null, blocksSend: false };
    }
    case 'recipient_not_allowed':
      return { key: 'pilot', action: null, blocksSend: false };
    case 'microsoft_card_changed':
      return { key: 'changed', action: null, blocksSend: true };
    case 'rate_limited':
      return { key: 'rateLimited', action: null, blocksSend: false };
    case 'microsoft_unavailable':
      return { key: 'unavailable', action: null, blocksSend: false };
    case 'microsoft_reconnect':
      return { key: 'reconnect', action: 'reconnect', blocksSend: false };
    case 'microsoft_not_connected':
      return { key: 'notConnected', action: 'reconnect', blocksSend: false };
    case 'microsoft_consent_required':
      return { key: 'consent', action: 'consent', blocksSend: false };
    case 'validation_failed':
      // a meeting moved to a time that passed or is too close; an edit that does not hold; a card that cannot go at all
      if (detail.fields?.some((field) => field.code === 'too_soon')) return { key: 'tooSoon', action: null, blocksSend: false };
      return { key: detail.edited === false ? 'invalidCard' : 'invalid', action: null, blocksSend: false };
    case 'network_error':
    case 'service_unavailable':
      return { key: kind === 'meeting' ? 'lostMeeting' : 'lost', action: null, blocksSend: true };
    default:
      return null;
  }
}

/** The primary button's words: "Gönder", "Oluştur ve davet et" (only "Oluştur" for a meeting with oneself), busy. */
export function primaryLabel(kind: Microsoft365WriteKind, attendees: number, sending: boolean): string {
  if (kind === 'mail') return sending ? 'sending' : 'send';
  if (sending) return 'creating';
  return attendees > 0 ? 'create' : 'createAlone';
}
